import type { Translate } from "@/lib/i18n/translate";

export interface LoginFormNotice {
  readonly tone: "info" | "error";
  readonly text: string;
}

/**
 * What the email-change confirmation link left behind in the URL.
 *
 * Better Auth redirects EVERY outcome of `/api/auth/verify-email` to this page
 * — success bare, failure with `&error=<CODE>`. The page used to drop both
 * without a word, so an expired link and a completed change looked exactly the
 * same: nothing. Whatever else is broken, the user must at least learn which
 * of the two happened.
 */
function emailChangeNotice(
  t: Translate,
  errorCode: string | null,
  portal: boolean,
): LoginFormNotice {
  if (errorCode === null) {
    return { tone: "info", text: t("emailGeaendertJetztAnmelden") };
  }
  switch (errorCode) {
    case "TOKEN_EXPIRED":
      // Portal has no „Einstellungen“: tenants change the login e-mail under „Profil“.
      return {
        tone: "error",
        text: portal
          ? t("bestaetigungslinkAbgelaufenPortal")
          : t("bestaetigungslinkAbgelaufen"),
      };
    case "INVALID_TOKEN":
      return { tone: "error", text: t("bestaetigungslinkUngueltig") };
    case "INVALID_USER":
      return { tone: "error", text: t("bestaetigungslinkAnderesKonto") };
    case "USER_NOT_FOUND":
      return { tone: "error", text: t("bestaetigungslinkKontoWeg") };
    default:
      return { tone: "error", text: t("bestaetigungFehlgeschlagen") };
  }
}

/** Notice from the sign-in URL: e-mail change outcome or an ended portal access. */
export function loginFormNotice(
  t: Translate,
  params: { get: (name: string) => string | null },
  portal: boolean,
): LoginFormNotice | null {
  if (params.get("email-changed") === "1") {
    return emailChangeNotice(t, params.get("error"), portal);
  }
  if (portal && params.get("revoked") === "1") {
    return { tone: "info", text: t("portalzugangBeendet") };
  }
  return null;
}
